import { Alert, Box, CircularProgress, Grid } from "@mui/material";
import { observer } from "mobx-react";
import { FC, useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import CompanyHeader from "./CompanyHeader/CompanyHeader";
import CompanySidebar from "./CompanySidebar/CompanySidebar";
import { useStore } from "src/Shared/Hooks";
import { IRouteParams } from "../../Interfaces";
import { useStyles } from "./CompanyManagementLayout.styles";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import { ROUTE_URLS } from "src/Shared/Enums/RouteUrls.enum";
import { ToastService } from "src/Shared/Services";
import { isClientArchived } from "../../Tools/ArchivedClient";

const CompanyManagementLayout: FC = () => {
  const {
    rootStore: {
      companyStore: {
        companyOverview,
        getCompanyOverview,
        overViewLoading,
        getCompanyMenu,
        menu,
        resetcompanyOverview,
        companyNotFound,
      },
    },
  } = useStore();

  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const classes = useStyles();
  const location = useLocation();
  useEffect(() => {
    let mode;
    if (location.pathname.includes("/add")) {
      mode = VIEW_MODE.ADD;
    } else if (location.pathname.includes("/archived")) {
      mode = VIEW_MODE.Archived;
    } else {
      mode = VIEW_MODE.EDIT;
    }

    if (mode == VIEW_MODE.EDIT) {
      getCompanyOverview(companyRowID);
      getCompanyMenu(companyRowID);
    } else {
      resetcompanyOverview();
      getCompanyMenu();
    }
  }, [companyRowID]);

  // Said once per miss, so the redirect below is not a silent bounce back to the grid — a stale
  // bookmark deserves to be told what happened to it (bug 5823).
  useEffect(() => {
    if (companyNotFound)
      ToastService.error("That client no longer exists. It may have been deleted.");
  }, [companyNotFound]);

  if (overViewLoading && !companyOverview && !menu) {
    return (
      <Box className={classes.progress}>
        <CircularProgress />
      </Box>
    );
  }

  /**
   * A client that is not there gets sent back to the grid rather than rendered.
   *
   * A hard-deleted client's GUID used to render this whole shell: an empty overview is also the
   * legitimate state of Add New Client, so every child fell back to its create-a-client placeholders
   * and a stale bookmark produced a phantom "NC / New Client" record — meta "No region · Unassigned ·
   * Employees TBD", a "TBD" status chip and a full editable sidebar (bug 5823).
   *
   * The store tells the two cases apart, so this only fires for a workspace that was asked for and
   * came back empty; Add New Client is untouched.
   */
  if (companyNotFound) {
    return <Navigate to={ROUTE_URLS.HOME} replace />;
  }

  // Opened by its own URL — a stale tab, a shared link, a bookmark — an archived client used to
  // render exactly like a live one: same status chip, same "Back to Prospects", every section
  // editable, with only its absence from the grids to notice by (task 5844). The header chip already
  // reads "Archived"; this puts the same fact somewhere impossible to miss regardless of which
  // section is open.
  const isArchivedMode = location.pathname.includes("/archived");
  const showsAsArchived = isArchivedMode || isClientArchived(companyOverview);

  return (
    <Box className={classes.root}>
      <CompanyHeader />

      {showsAsArchived && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          This client has been archived and is read-only. It no longer appears in any grid or
          search.
        </Alert>
      )}

      <Grid container>
        <Grid item xs={12} md={2.5} lg={1.8}>
          {menu && menu.length > 0 && <CompanySidebar />}
        </Grid>

        <Grid item xs={12} md={9.5} lg={10.2}>
          <Box sx={{ p: 2 }}>
            <Outlet context={{ companyOverview }} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default observer(CompanyManagementLayout);
